// Resolves a retail channel id to its name for the registrationSubmission
// dataLayer event.
//
// receiptData.retailChannel holds a retailChannels _id whether it was picked in
// purchase details or in a form question linked to the global retailChannel
// property. The two pickers render separate lists, both shaped
// { value: <retailChannels _id>, label: <name> }: purchase details uses
// registration.purchaseTemplate.retailChannelsList, a form question uses its own
// config.channels. Searching only the purchase details list pushed the raw id
// for form flows. The server resolves the same id against the retailChannels
// collection and emits its name, so a label from any of these lists matches the
// webhook's registrationPurchaseChannel.
//
// Returns null when no list resolves the id, so the caller keeps its raw id
// fallback. Returns '' only when the matching purchase details option's own
// label is '', exactly as the original lookup did.
//
// Pure and never throws: it runs inside the registration submission event,
// after the registration has already succeeded.

const RETAIL_CHANNEL_QUESTION = 'retail-channel';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

const findOptionLabel = (options: unknown, id: string): string | null => {
  if (!Array.isArray(options)) return null;
  for (const option of options) {
    if (
      option &&
      typeof option === 'object' &&
      option.value === id &&
      isNonEmptyString(option.label)
    ) {
      return option.label;
    }
  }
  return null;
};

const findQuestionLabel = (questions: unknown, id: string): string | null => {
  if (!Array.isArray(questions)) return null;
  for (const question of questions) {
    if (question?.type !== RETAIL_CHANNEL_QUESTION) continue;
    const label = findOptionLabel(question?.config?.channels, id);
    if (label) return label;
  }
  return null;
};

const findAnswerLabel = (entries: unknown, id: string): string | null => {
  if (!Array.isArray(entries)) return null;
  for (const entry of entries) {
    const answer = entry?.answer;
    if (
      answer &&
      typeof answer === 'object' &&
      !Array.isArray(answer) &&
      answer.custom !== true &&
      answer.value === id &&
      isNonEmptyString(answer.label)
    ) {
      return answer.label;
    }
  }
  return null;
};

const resolveRetailChannelLabel = (
  id: unknown,
  details: any,
  storedFormResponse?: { responseWithProperty?: unknown } | null
): string | null => {
  try {
    if (!isNonEmptyString(id)) return null;

    // 1. Purchase details list first, with the original lookup's semantics
    // (first option whose value matches, its label returned as-is even when
    // it is ''), so that flow's output is unchanged.
    const purchaseDetailsList =
      details?.registration?.purchaseTemplate?.retailChannelsList;
    if (Array.isArray(purchaseDetailsList)) {
      const match = purchaseDetailsList.find(
        (option: any) =>
          option && typeof option === 'object' && option.value === id
      );
      if (match && typeof match.label === 'string') return match.label;
    }

    // 2. The registration form's own channel questions.
    const registrationFormLabel = findQuestionLabel(
      details?.registration?.formModule?.moduleInfo,
      id
    );
    if (registrationFormLabel) return registrationFormLabel;

    // 3. Standalone form modules on the experience. They share receiptData
    // with registration, so a channel picked there can reach the register call.
    if (Array.isArray(details?.modules)) {
      for (const module of details.modules) {
        const moduleLabel = findQuestionLabel(module?.moduleInfo, id);
        if (moduleLabel) return moduleLabel;
      }
    }

    // 4. The stored registration form answer keeps the label that was picked.
    return findAnswerLabel(storedFormResponse?.responseWithProperty, id);
  } catch {
    return null;
  }
};

export default resolveRetailChannelLabel;
